import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SMSParser from './SMSParser';
import OrdersList from './OrdersList';
import CrudManager from './CrudManager';
import CreateOrder from './CreateOrder';

const API_URL = '/api';

const Dashboard = ({ user, token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [showCreateOrder, setShowCreateOrder] = useState(false);

  // Загрузка заявок
  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`);
      setOrders(response.data);
    } catch (err) {
      console.error('Ошибка загрузки заявок:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab]);

  // Конфигурация для CRUD менеджеров
  const crudConfigs = {
    cars: {
      title: '🚗 Автомобили',
      columns: [
        { header: 'Госномер', field: 'plate_number' },
        { header: 'Модель', field: 'model' },
        { header: 'Грузоподъёмность', field: 'capacity', render: (item) => item.capacity ? `${item.capacity} кг` : '—' },
        { header: 'Статус', field: 'status', render: (item) => {
            const statusMap = { active: '✅ Активен', maintenance: '🔧 В ремонте', retired: '❌ Списан' };
            return statusMap[item.status] || item.status;
          }
        }
      ],
      formFields: [
        { label: 'Госномер', name: 'plate_number', type: 'text', placeholder: 'А123ВС777' },
        { label: 'Модель', name: 'model', type: 'text', placeholder: 'ГАЗель Next' },
        { label: 'Грузоподъёмность (кг)', name: 'capacity', type: 'number', placeholder: '1500' },
        { label: 'Статус', name: 'status', type: 'select', options: [
            { value: 'active', label: 'Активен' },
            { value: 'maintenance', label: 'В ремонте' },
            { value: 'retired', label: 'Списан' }
          ]
        }
      ]
    },
    drivers: {
      title: '👨 Водители',
      columns: [
        { header: 'ФИО', field: 'name' },
        { header: 'Телефон', field: 'phone' },
        { header: 'Номер прав', field: 'license_number' },
        { header: 'Статус', field: 'status', render: (item) => {
            const statusMap = { active: '✅ Активен', vacation: '🏖️ В отпуске', fired: '❌ Уволен' };
            return statusMap[item.status] || item.status;
          }
        }
      ],
      formFields: [
        { label: 'ФИО', name: 'name', type: 'text', placeholder: 'Иванов Иван Иванович' },
        { label: 'Телефон', name: 'phone', type: 'text', placeholder: '+7 (999) 123-45-67' },
        { label: 'Номер прав', name: 'license_number', type: 'text', placeholder: '77AB123456' },
        { label: 'Статус', name: 'status', type: 'select', options: [
            { value: 'active', label: 'Активен' },
            { value: 'vacation', label: 'В отпуске' },
            { value: 'fired', label: 'Уволен' }
          ]
        }
      ]
    },
    routes: {
      title: '🗺️ Маршруты',
      columns: [
        { header: 'Название', field: 'name' },
        { header: 'Откуда', field: 'start_point' },
        { header: 'Куда', field: 'end_point' },
        { header: 'Расстояние', field: 'distance', render: (item) => item.distance ? `${item.distance} км` : '—' }
      ],
      formFields: [
        { label: 'Название', name: 'name', type: 'text', placeholder: 'Москва - СПБ' },
        { label: 'Откуда', name: 'start_point', type: 'text', placeholder: 'Москва, ул. Примерная 1' },
        { label: 'Куда', name: 'end_point', type: 'text', placeholder: 'СПБ, Невский пр. 1' },
        { label: 'Расстояние (км)', name: 'distance', type: 'number', placeholder: '700' }
      ]
    },
    counterparties: {
      title: '🏢 Контрагенты',
      columns: [
        { header: 'Название', field: 'name' },
        { header: 'Контактное лицо', field: 'contact_person' },
        { header: 'Телефон', field: 'phone' },
        { header: 'Адрес', field: 'address' }
      ],
      formFields: [
        { label: 'Название', name: 'name', type: 'text', placeholder: 'ООО "Пример"' },
        { label: 'Контактное лицо', name: 'contact_person', type: 'text', placeholder: 'Иванов И.И.' },
        { label: 'Телефон', name: 'phone', type: 'text', placeholder: '+7 (999) 123-45-67' },
        { label: 'Адрес', name: 'address', type: 'text', placeholder: 'г. Москва, ул. Примерная 1' },
        { label: 'ИНН', name: 'inn', type: 'text', placeholder: '7701234567' }
      ]
    },
    users: {
      title: '👥 Пользователи',
      columns: [
        { header: 'Логин', field: 'login' },
        { header: 'Роль', field: 'role', render: (item) => item.role === 'admin' ? '👑 Админ' : '👤 Сотрудник' },
        { header: 'Активен', field: 'is_active', render: (item) => item.is_active ? '✅ Да' : '❌ Нет' },
        { header: 'Создан', field: 'created_at', render: (item) => new Date(item.created_at).toLocaleDateString('ru-RU') }
      ],
      formFields: [
        { label: 'Логин', name: 'login', type: 'text', placeholder: 'user@example.com' },
        { label: 'Пароль', name: 'password', type: 'password', placeholder: '******' },
        { label: 'Роль', name: 'role', type: 'select', options: [
            { value: 'user', label: 'Сотрудник' },
            { value: 'admin', label: 'Админ' }
          ]
        }
      ]
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>🚚 Система управления заявками</h1>
        <div style={styles.userInfo}>
          <span>👤 {user?.role === 'admin' ? 'Админ' : 'Сотрудник'}</span>
          <button onClick={onLogout} style={styles.logoutBtn}>Выход</button>
        </div>
      </header>

      <nav style={styles.nav}>
        <button 
          onClick={() => setActiveTab('orders')} 
          style={{ ...styles.navBtn, background: activeTab === 'orders' ? '#075A64' : '#ccc' }}
        >
          📋 Заявки
        </button>
        <button 
          onClick={() => setActiveTab('sms')} 
          style={{ ...styles.navBtn, background: activeTab === 'sms' ? '#075A64' : '#ccc' }}
        >
          📱 Из SMS
        </button>
        <button 
          onClick={() => setActiveTab('cars')} 
          style={{ ...styles.navBtn, background: activeTab === 'cars' ? '#075A64' : '#ccc' }}
        >
          🚗 Автомобили
        </button>
        <button 
          onClick={() => setActiveTab('drivers')} 
          style={{ ...styles.navBtn, background: activeTab === 'drivers' ? '#075A64' : '#ccc' }}
        >
          👨 Водители
        </button>
        <button 
          onClick={() => setActiveTab('routes')} 
          style={{ ...styles.navBtn, background: activeTab === 'routes' ? '#075A64' : '#ccc' }}
        >
          🗺️ Маршруты
        </button>
        <button 
          onClick={() => setActiveTab('counterparties')} 
          style={{ ...styles.navBtn, background: activeTab === 'counterparties' ? '#075A64' : '#ccc' }}
        >
          🏢 Контрагенты
        </button>
        {user?.role === 'admin' && (
          <button 
            onClick={() => setActiveTab('users')} 
            style={{ ...styles.navBtn, background: activeTab === 'users' ? '#075A64' : '#ccc' }}
          >
            👥 Пользователи
          </button>
        )}
      </nav>

      <main style={styles.main}>
        {activeTab === 'orders' && (
          <div>
            <div style={styles.ordersHeader}>
              <h2 style={{margin: 0}}>📋 Список заявок</h2>
              <button 
                onClick={() => setShowCreateOrder(true)}
                style={styles.btnCreateOrder}
              >
                ➕ Создать заявку
              </button>
            </div>
            <OrdersList orders={orders} onRefresh={loadOrders} />
          </div>
        )}
        {activeTab === 'sms' && <SMSParser onOrderCreated={loadOrders} />}
        {activeTab === 'cars' && <CrudManager {...crudConfigs.cars} entityType="cars" />}
        {activeTab === 'drivers' && <CrudManager {...crudConfigs.drivers} entityType="drivers" />}
        {activeTab === 'routes' && <CrudManager {...crudConfigs.routes} entityType="routes" />}
        {activeTab === 'counterparties' && <CrudManager {...crudConfigs.counterparties} entityType="counterparties" />}
        {activeTab === 'users' && user?.role === 'admin' && <CrudManager {...crudConfigs.users} entityType="users" />}
        
        {/* Форма создания заявки */}
        {showCreateOrder && (
          <CreateOrder
            onClose={() => setShowCreateOrder(false)}
            onSuccess={() => {
              setShowCreateOrder(false);
              loadOrders();
            }}
          />
        )}
      </main>
    </div>
  );
};

const styles = {
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f5f5',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #075A64 0%, #0C7281 100%)',
    color: '#FEFEFA',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    '@media (max-width: 768px)': {
      fontSize: '16px',
    },
  },
  userInfo: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  logoutBtn: {
    padding: '8px 15px',
    background: '#FEFEFA',
    color: '#075A64',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    '@media (max-width: 768px)': {
      padding: '6px 12px',
      fontSize: '12px',
    },
  },
  nav: {
    display: 'flex',
    gap: '10px',
    padding: '15px',
    background: '#fff',
    borderBottom: '2px solid #0C7281',
    flexWrap: 'wrap',
    overflowX: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
  },
  navBtn: {
    padding: '10px 15px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    color: 'white',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    '@media (max-width: 768px)': {
      padding: '8px 12px',
      fontSize: '12px',
    },
  },
  main: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    '@media (max-width: 768px)': {
      padding: '10px',
    },
  },
  ordersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  btnCreateOrder: {
    padding: '10px 20px',
    background: '#075A64',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    '@media (max-width: 768px)': {
      width: '100%',
      padding: '12px 20px',
    },
  },
};

export default Dashboard;
